import { Link } from "react-router-dom";
import Seo from "@/lib/Seo";
import { FadeUp } from "@/lib/motion";
import { SITE, IMG } from "@/data/content";

export default function About() {
  return (
    <div className="pt-[72px]">
      <Seo
        title="About Marble & Veil — Family Owned, Southern California"
        description="Marble & Veil began with planning our own wedding. Meet Sandra and Nathan, a family-owned studio coordinating personalized wedding details across Southern California."
        path="/about"
      />
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <FadeUp>
            <div className="mat-frame">
              <div className="overflow-hidden">
                <img src={IMG.familyTable} alt="A table set by hand for a family gathering — design inspiration" className="aspect-[4/5] w-full object-cover" width="800" height="1000" loading="lazy" />
              </div>
            </div>
            <p className="mt-3 text-[11px] uppercase tracking-widest2 text-smoke">Design inspiration</p>
          </FadeUp>
          <div>
            <FadeUp>
              <p className="mv-label">Our Story</p>
              <h1 className="mt-3 font-display text-4xl leading-[1.08] text-ink sm:text-5xl">
                It began with our own weddings.
              </h1>
              <p className="mt-6 leading-relaxed text-smoke">
                Marble &amp; Veil began with planning our own weddings. We saw how quickly the costs and
                decisions could add up, especially when every detail meant finding another vendor. That
                experience shaped a simple idea: bring beautiful, personal wedding details together in a
                more thoughtful, approachable way. From the first welcome to the tablescape and guest
                experience, we help couples create a celebration that feels like theirs.
              </p>
              <p className="mt-5 max-w-xl leading-relaxed text-smoke">
                Marble &amp; Veil is family owned and operated. When you write to us, you reach the
                family directly — the same hands guide every detail, from the first welcome to the
                final piece of the tablescape.
              </p>
              <p className="mt-6 max-w-xl border-l-2 border-gilt pl-5 font-display text-2xl italic leading-snug text-ink">
                Two families, one goal — bring the elegance to brides and grooms without the stress of
                multiple vendors.
              </p>
              <p className="mt-5 font-display text-2xl italic text-olive">{SITE.owners}</p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="mt-10 grid gap-px bg-ink/10 sm:grid-cols-2">
                {[
                  ["Family owned & operated", "Marble & Veil is run by our family — Sandra & Nathan and Alexandra & Roman — personally, from first welcome to final detail."],
                  ["Coordinated, not chaotic", "One place for goods, decor, and guest experiences — so the details speak the same language."],
                  ["Honest availability", "An inquiry never guarantees a date, stock, or staffing. We confirm everything together, in writing."],
                  ["Priced in a proposal", "Every celebration is scoped individually. No prices by the row, no pressure to upsell."],
                ].map(([t, d]) => (
                  <div key={t} className="bg-ivory p-7">
                    <h2 className="font-display text-xl text-ink">{t}</h2>
                    <p className="mt-2 text-sm leading-relaxed text-smoke">{d}</p>
                  </div>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.15}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/quote" data-testid="about-quote-link" className="rounded-none bg-olive px-9 py-4 text-[12px] uppercase tracking-[0.18em] text-ivory transition-transform duration-300 hover:-translate-y-0.5">
                  Request a Quote
                </Link>
                <Link to="/gallery" data-testid="about-gallery-link" className="border border-ink/25 px-9 py-4 text-[12px] uppercase tracking-[0.18em] text-ink transition-colors hover:border-olive hover:text-olive">
                  See the Gallery
                </Link>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
